import type { Key } from 'react-aria-components'
import type { SelectListItemData } from '../_components/SelectListItem.client'
import type { SelectListSectionData } from '../_components/SelectListSection.client'
import { SELECT_ALL_ID } from '../select.styles'

export function isSection(
  item: SelectListItemData | SelectListSectionData,
): item is SelectListSectionData {
  return 'items' in item
}

export function toSingleFormValue(key: Key | null) {
  if (key == null) return ''

  return String(key)
}

export function toMultipleFormValue(keys: Key[]) {
  return keys.map(String).filter((key) => key !== SELECT_ALL_ID)
}

export function toSelectValue(selectionMode: 'single' | 'multiple', fieldValue: unknown) {
  if (selectionMode === 'multiple') {
    if (Array.isArray(fieldValue)) return fieldValue

    return []
  }

  if (typeof fieldValue === 'string') {
    return fieldValue === '' ? null : fieldValue
  }

  if (typeof fieldValue === 'number') return fieldValue

  return null
}

export function getSelectableIds(items: SelectListItemData[] | SelectListSectionData[]): string[] {
  return items.flatMap((item) =>
    isSection(item) ? item.items.map((child) => String(child.id)) : [String(item.id)],
  )
}

export function getFlatItems(
  items: SelectListItemData[] | SelectListSectionData[],
  isMultiple: boolean,
  selectAllLabel: string | undefined,
): SelectListItemData[] | null {
  if (!isMultiple || !selectAllLabel) return null
  if (items.some(isSection)) return null

  return items.filter((item): item is SelectListItemData => !isSection(item))
}

export function getListItems(
  items: SelectListItemData[] | SelectListSectionData[],
  flatItems: SelectListItemData[] | null,
  selectAllLabel: string | undefined,
): SelectListItemData[] | SelectListSectionData[] {
  if (!flatItems || !selectAllLabel) return items

  return [{ id: SELECT_ALL_ID, label: selectAllLabel }, ...flatItems]
}

export function getNextMultipleValue(
  keys: Key[],
  selectAllLabel: string | undefined,
  allSelected: boolean,
  allItemIds: string[],
): string[] {
  const hasSelectAll = keys.map(String).includes(SELECT_ALL_ID)
  const realKeys = toMultipleFormValue(keys)

  if (!selectAllLabel) return realKeys
  if (hasSelectAll && !allSelected) return allItemIds
  // Uncheck select-all (all item keys still present). Partial uncheck keeps a shorter list.
  if (!hasSelectAll && allSelected && realKeys.length === allItemIds.length) return []

  return realKeys
}

export function areAllItemsSelected(
  allItemIds: readonly string[],
  selectedIds: readonly string[],
): boolean {
  if (allItemIds.length === 0) return false

  const selectedIdSet = new Set(selectedIds)

  return allItemIds.every((id) => selectedIdSet.has(id))
}

/** Drops removed TagGroup keys from a multi Select form value. */
export function removeKeysFromValue(fieldValue: unknown, keys: Iterable<Key>): string[] {
  const current = Array.isArray(fieldValue) ? fieldValue.map(String) : []
  const removed = new Set([...keys].map(String))

  return current.filter((id) => !removed.has(id))
}

export function constrainSelectionCount(
  next: string[],
  current: string[],
  minSelected?: number,
  maxSelected?: number,
): string[] {
  if (maxSelected != null && next.length > maxSelected) return current
  if (minSelected != null && next.length < minSelected) return current

  return next
}

export function isSameSelection(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false

  const rightSet = new Set(right)

  return left.every((id) => rightSet.has(id))
}

/** Keys that cannot be toggled without breaking min/max. Includes select-all when it would violate. */
export function getDisabledSelectionKeys({
  selectableIds,
  selectedIds,
  minSelected,
  maxSelected,
  hasSelectAll,
}: {
  selectableIds: readonly string[]
  selectedIds: readonly string[]
  minSelected?: number
  maxSelected?: number
  hasSelectAll: boolean
}): string[] {
  if (minSelected == null && maxSelected == null) return []

  const selectedIdSet = new Set(selectedIds)
  const count = selectedIds.length
  const atMin = minSelected != null && count <= minSelected
  const atMax = maxSelected != null && count >= maxSelected
  const disabled: string[] = []

  for (const id of selectableIds) {
    const isSelected = selectedIdSet.has(id)

    if ((atMax && !isSelected) || (atMin && isSelected)) {
      disabled.push(id)
    }
  }

  if (hasSelectAll) {
    const cannotSelectAll = maxSelected != null && selectableIds.length > maxSelected
    const cannotClearAll =
      minSelected != null && minSelected > 0 && areAllItemsSelected(selectableIds, selectedIds)

    if (cannotSelectAll || cannotClearAll) {
      disabled.push(SELECT_ALL_ID)
    }
  }

  return disabled
}
